"use client";

import {
  ctaClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { ArrowUpRight } from "lucide-react";
import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

export interface Enrollments3Data {
  id?: string;
  status?: boolean;
  title?: string;
  description?: string;
  cta_label?: string;
  cta_url?: string;
  open_in_new_tab?: boolean;
  desktop_image?: string;
  mobile_image?: string;
  background_color?: string;
  overlay_color?: string;
  overlay_opacity?: number | string;
  title_color?: string;
  title_highlight_color?: string;
  description_color?: string;
  cta_background_color?: string;
  cta_text_color?: string;
  enable_lead_popup?: boolean;
}

interface Enrollments3Props {
  data: Enrollments3Data | { data: Enrollments3Data };
}

function resolvePayload(
  data: Enrollments3Props["data"],
): Enrollments3Data | undefined {
  const anyData = data as { data?: Enrollments3Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Enrollments3Data;
  }
  return data as Enrollments3Data;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Wraps `{highlight}` / `{{line}}` segments in a span styled with the supplied
 * highlight color. Mirrors `formatSectionTitle` semantics but applies the color
 * via inline `style=` so admin-driven hex values don't need Tailwind JIT.
 */
function formatTitleWithHighlight(title: string, highlightColor: string): string {
  if (!title) return "";
  const safeColor = highlightColor.replace(/[^#a-zA-Z0-9(),.%\s]/g, "");
  const openTag = `<span style="color:${safeColor}">`;

  if (title.includes("{{")) {
    return title
      .replace(/\{\{([^}]+)\}\}/g, (_m, inner) => `${openTag}${escapeHtml(inner)}</span><br/>`)
      .replace(/\s+/g, " ");
  }

  if (title.includes("{")) {
    return title
      .replace(/\{/g, openTag)
      .replace(/\}/g, "</span>")
      .replace(/\s+/g, " ");
  }

  return escapeHtml(title);
}

function clampOpacity(value: number | string | undefined): number {
  if (value === undefined || value === null || value === "") return 0.5;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return 0.5;
  if (n > 1) return Math.min(n / 100, 1);
  return Math.max(0, Math.min(n, 1));
}

const Enrollments3 = ({ data }: Enrollments3Props) => {
  const dispatch = useDispatch();
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);

  const {
    status = true,
    title = "Why Pursue this Programme",
    description = "Take the next step in your career",
    cta_label = "Apply Now",
    cta_url = "",
    open_in_new_tab = true,
    desktop_image = "",
    mobile_image = "",
    background_color = "#FFFFFF",
    overlay_color = "#000000",
    overlay_opacity = 0.5,
    title_color = "#FFFFFF",
    title_highlight_color = "#FFD700",
    description_color = "#FFFFFF",
    cta_background_color = "#FFFFFF",
    cta_text_color = "#DB5803",
    enable_lead_popup: enableLeadRaw,
  } = sectionData || {};

  const raw = sectionData as Record<string, unknown> | undefined;
  const enableLeadPopup = isCmsShowFlagOn(
    raw?.enable_lead_popup ?? raw?.enableLeadPopup ?? enableLeadRaw,
    true,
  );

  const hrefTrim = (cta_url || "").trim();
  const ctaOpensLeadPopup = enableLeadPopup || !hrefTrim;
  const opacity = clampOpacity(overlay_opacity);

  const openLeadFromCta = useCallback(() => {
    ctaClick({
      section: "enrollments_3",
      component: "cta_apply",
      label: cta_label?.trim() || "apply",
      step: "1",
      entity: "enrollments",
      parentEntity: "enrollments_3",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [cta_label, dispatch]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "enrollments_3");
  //   }
  // }, [status]);

  if (!status) return null;

  const ctaClassName = cn(
    "inline-flex shrink-0 items-center gap-2 rounded-md font-semibold shadow-sm transition-opacity",
    "px-4 py-2 text-sm leading-snug hover:opacity-95",
    "min-[768px]:gap-2.5 min-[768px]:px-5 min-[768px]:py-3 min-[768px]:text-base min-[768px]:leading-[23px]",
    ctaOpensLeadPopup && "cursor-pointer",
    !ctaOpensLeadPopup && !hrefTrim && "cursor-default opacity-90",
  );

  const ctaIconClass = "size-5 shrink-0 min-[768px]:size-6";

  const ctaStyle = {
    backgroundColor: cta_background_color,
    color: cta_text_color,
  };

  const renderCta = () => {
    if (ctaOpensLeadPopup) {
      return (
        <button
          type="button"
          className={ctaClassName}
          style={ctaStyle}
          onClick={openLeadFromCta}
        >
          {cta_label}
          <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
        </button>
      );
    }
    if (hrefTrim) {
      return (
        <a
          href={hrefTrim}
          target={open_in_new_tab ? "_blank" : undefined}
          rel={open_in_new_tab ? "noopener noreferrer" : undefined}
          className={ctaClassName}
          style={ctaStyle}
          onClick={() =>
            ctaClick({
              section: "enrollments_3",
              component: "cta_apply_link",
              label: cta_label?.trim() || "apply",
              step: "1",
              entity: "enrollments",
              parentEntity: "enrollments_3",
            })
          }
        >
          {cta_label}
          <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
        </a>
      );
    }
    return (
      <span className={cn(ctaClassName, "cursor-default opacity-90")} style={ctaStyle}>
        {cta_label}
        <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
      </span>
    );
  };

  const desktopBg = (desktop_image || "").trim();
  const mobileBg = (mobile_image || desktop_image || "").trim();

  return (
    <section
      ref={ref}
      data-enrollments="3"
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "mx-auto flex w-full items-center justify-center p-0",
        "min-[1024px]:max-w-screen-container",
      )}
      style={{
        backgroundColor: background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div className="flex w-full flex-col items-center justify-center gap-[30px] bg-transparent p-0 min-[1024px]:bg-white min-[1024px]:px-[38px] min-[1024px]:py-[52px]">
        <div
          className={cn(
            "relative flex h-auto w-full shrink-0 flex-col items-center justify-center gap-[28px] overflow-hidden py-[50px]",
            // max-w-full guards the fixed 1040px: the wrapper caps content at
            // 1114 - 38*2 = 1038px, so a shrink-0 card would overflow the page
            // (worst at 1024px, where only ~948px is available).
            "min-[1024px]:h-[266px] min-[1024px]:w-[1040px] min-[1024px]:max-w-full min-[1024px]:rounded-[16px] min-[1024px]:py-[50px]",
          )}
          style={{
            backgroundImage: desktopBg ? `url(${desktopBg})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Mobile background image overlay */}
          <div
            className="absolute inset-0 bg-cover bg-center min-[1024px]:hidden"
            style={{
              backgroundImage: mobileBg ? `url(${mobileBg})` : undefined,
            }}
          />

          {/* Tint overlay for text readability */}
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundColor: overlay_color,
              opacity,
            }}
          />

          {/* Content */}
          <div className="relative z-10 flex w-full flex-col items-center justify-center gap-[28px] px-4 min-[1024px]:px-0">
            {title ? (
              <h2
                className="text-center text-[28px] font-semibold leading-tight min-[1024px]:text-[40px]"
                style={{ color: title_color }}
                dangerouslySetInnerHTML={{
                  __html: formatTitleWithHighlight(title, title_highlight_color),
                }}
              />
            ) : null}
            {description ? (
              <p
                className="w-[296px] text-center font-poppins text-[22px] font-semibold leading-normal min-[1024px]:w-auto min-[1024px]:text-[22px]"
                style={{ color: description_color }}
              >
                {description}
              </p>
            ) : null}
            {renderCta()}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Enrollments3;
